import { cn, formatNumberWithZeroCount } from "utils";
import { memo, useMemo, useRef } from "react";
import { BaseColumnProps, zeroReturn } from "../../types";

import { isStringNotZero } from "../formatDisplay";
import { CRYPTO_THEME_LABELS } from "@/src/componentsv1";
import { formatThousands } from "@/src/utils";
import { usePriceGradient } from "@/src/hooks/usePriceGradient";
import { useTokenField } from "@hooks";

// 提取价格显示为单独的组件
const PriceDisplay = memo(
  ({
    integerPart,
    leftDigits,
    zeroCount,
    fontColor,
    gradientClassName,
    isFlashing,
    customClassName,
  }: {
    integerPart: string;
    leftDigits?: string | null;
    zeroCount?: number;
    fontColor?: string;
    gradientClassName?: string;
    isFlashing?: boolean;
    customClassName?: string; // 支持外部传入自定义类名
  }) => {
    // 计算最终的类名和样式
    const finalClassName = gradientClassName || "text-content_primary";
    // 文字颜色动画时，让CSS动画接管颜色控制，否则使用fontColor
    const finalStyle = gradientClassName?.includes("price-gradient")
      ? {} // 动画时不设置color，让CSS动画控制
      : { color: fontColor }; // 非动画时使用 fontColor

    if (!integerPart) {
      return (
        <span
          className={cn(
            finalClassName,
            "body_medium_m",
            "price-flash",
            isFlashing ? "flash" : "",
            customClassName, // 添加自定义类名支持
          )}
          style={finalStyle}
        >
          {zeroReturn}
        </span>
      );
    }

    return (
      <span
        className={cn(
          finalClassName,
          "body_medium_m",
          "price-flash",
          isFlashing ? "flash" : "",
          customClassName, // 添加自定义类名支持
        )}
        style={finalStyle}
      >
        ${formatThousands(integerPart)}
        {leftDigits ? (
          zeroCount ? (
            <>
              .0
              <span
                className={cn(
                  finalClassName,
                  "body_medium_xs",
                  "price-flash",
                  isFlashing ? "flash" : "",
                  customClassName, // 添加自定义类名支持
                )}
                style={{
                  position: "relative",
                  bottom: "-2px",
                  ...finalStyle,
                }}
              >
                {zeroCount}
              </span>
              {leftDigits}
            </>
          ) : (
            `.${leftDigits}`
          )
        ) : (
          ""
        )}
      </span>
    );
  },
);

PriceDisplay.displayName = "PriceDisplay";

export const PriceColumn = memo(
  ({ record, panelType, fontColor, className }: BaseColumnProps) => {
    const latestValueRef = useRef<string | null>(null);

    const { value } = useTokenField({
      poolAddress: record.poolAddress || record.id,
      fieldKey: "price_usdt",
      fallbackValue: latestValueRef.current || record?.trade?.price_usdt,
      panelType,
    });
    // 更新最新值
    if (value) {
      latestValueRef.current = value;
    }

    // 加个一定是有意义的 !!Number
    const originalPrice = isStringNotZero(value)
      ? value
      : latestValueRef.current || record?.trade?.price_usdt;

    // 使用 mock 价格或原价格
    const price = originalPrice;

    // 新增：集成价格渐变 hook
    const { gradientClassName, isFlashing } = usePriceGradient({
      currentPrice: price ? Number(price) : null,
      defaultClassName: "text-content_primary",
    });

    // 缓存价格计算结果
    const priceData = useMemo(() => {
      return formatNumberWithZeroCount({
        numStr: price,
        decimals: 6,
        minZeroCount: 4,
      });
    }, [price]);

    // 缓存左侧数字计算结果
    const leftDigits = useMemo(() => {
      if (!priceData?.isSuccess || panelType !== CRYPTO_THEME_LABELS.blueChip) {
        return priceData?.leftDigits;
      }

      const integerPart = Number(priceData.integerPart);
      if (integerPart > 0 && priceData?.leftDigits?.length > 2) {
        return priceData.leftDigits?.substring(0, 2);
      } else if (integerPart === 0 && priceData?.leftDigits?.length > 4) {
        return Number(priceData.leftDigits?.substring(0, 4) || 0) !== 0
          ? priceData.leftDigits?.substring(0, 4)
          : null;
      }
      return priceData?.leftDigits;
    }, [panelType, priceData]);

    if (!priceData?.isSuccess) {
      return (
        <PriceDisplay
          integerPart=""
          fontColor={fontColor}
          gradientClassName={gradientClassName}
          isFlashing={isFlashing}
          customClassName={className}
        />
      );
    }

    return (
      <PriceDisplay
        integerPart={priceData.integerPart}
        leftDigits={leftDigits}
        zeroCount={priceData.zeroCount}
        fontColor={fontColor}
        gradientClassName={gradientClassName}
        isFlashing={isFlashing}
        customClassName={className}
      />
    );
  },
);

PriceColumn.displayName = "PriceColumn";
